import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState, useId, memo } from 'react';
import { View, Text, Flex, IconButton, IconArrowOpenEndLine } from '@smarterservices/smarter-ui';
import TimelineItem from './TimelineItem';

/**
 * Timeline component displays a vertical timeline with customizable items.
 * Each item can include an icon, label, title, and optional detail content.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of timeline items to display
 * @param {Object} props.items[].icon - Icon configuration object
 * @param {React.ReactElement} props.items[].icon.icon - React element to display as the icon
 * @param {string} [props.items[].icon.color='primary'] - Color of the icon
 * @param {string} [props.items[].icon.size='small'] - Size of the icon
 * @param {string} [props.items[].icon.shape='circle'] - Shape of the icon ('circle' or 'rectangle')
 * @param {Function} [props.items[].icon.onClick] - Function to call when icon is clicked
 * @param {Function} [props.items[].icon.onContextMenu] - Function to call on right-click (context menu)
 * @param {string} [props.items[].icon.screenReaderLabel='Timeline Item'] - Accessibility label
 * @param {string|React.ReactElement} props.items[].label - Label text, typically a timestamp or date
 * @param {string|React.ReactElement} props.items[].title - Title text or component
 * @param {string|React.ReactElement} [props.items[].detail] - Additional details to display below the title
 * @param {string} [props.items[].id] - Optional unique identifier for the timeline item
 * @param {number} [props.items[].time] - Optional time value for auto-scrolling targeting
 * @param {*} [props.currentTime] - Current time value for auto-scrolling (from any source)
 * @param {boolean} [props.autoScroll=false] - Enable auto-scrolling functionality
 * @param {boolean} [props.showAutoScrollToggle=false] - Show floating auto-scroll toggle button
 * @param {Object} [props.autoScrollOptions] - Auto-scroll configuration options
 * @param {number} [props.topOffset=0] - Additional top offset in pixels when auto-scrolling
 * @param {boolean} [props.highlightScrollTarget=false] - Highlight the background of the item that was scrolled to
 * @returns {React.ReactElement} The rendered Timeline component
 * 
 * @example
 * // Basic usage with imperative scrolling
 * const timelineRef = useRef();
 * timelineRef.current?.scrollToTime(125.5);
 * 
 * @example
 * // With built-in auto-scrolling and toggle
 * const MyComponent = () => {
 *   const currentTime = usePlayerStore(state => state.currentTime);
 *   
 *   return (
 *     <Timeline 
 *       items={timelineData}
 *       currentTime={currentTime}
 *       autoScroll={true}
 *       showAutoScrollToggle={true}
 *     />
 *   );
 * };
 */
const Timeline = memo(forwardRef(({
  items,
  currentTime,
  autoScroll = false,
  showAutoScrollToggle = false,
  autoScrollOptions = {},
  topOffset = 0,
  highlightScrollTarget = false
}, ref) => {
  const containerRef = useRef(null);
  const baseId = useId();
  const timelineRef = useRef(null);

  // Performance optimization: Cache DOM queries and scroll parent
  const timeElementsCacheRef = useRef(null);
  const scrollParentCacheRef = useRef(null);
  const itemsHashRef = useRef(null);

  // Internal auto-scroll state
  const [internalAutoScrollEnabled, setInternalAutoScrollEnabled] = useState(autoScroll);

  // State for highlighting scroll target
  const [highlightedTime, setHighlightedTime] = useState(null);

  /**
   * Invalidate caches when items change
   */
  useEffect(() => {
    const newHash = items.map(item => item.id || item.time).join(',');
    if (itemsHashRef.current !== newHash) {
      timeElementsCacheRef.current = null;
      itemsHashRef.current = newHash;
    }
  }, [items]);

  /**
   * Get cached time elements or query and cache them
   */
  const getTimeElements = () => {
    if (!timeElementsCacheRef.current && containerRef.current) {
      timeElementsCacheRef.current = containerRef.current.querySelectorAll('[data-time]');
    }
    return timeElementsCacheRef.current;
  };

  /**
   * Get cached scroll parent or find and cache it
   */
  const getScrollParent = () => {
    if (scrollParentCacheRef.current) {
      return scrollParentCacheRef.current;
    }

    if (!containerRef.current) return null;

    let scrollableParent = containerRef.current;
    while (scrollableParent && scrollableParent !== document.body) {
      const style = window.getComputedStyle(scrollableParent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll' || scrollableParent === document.documentElement) {
        break;
      }
      scrollableParent = scrollableParent.parentElement;
    }

    scrollParentCacheRef.current = scrollableParent;
    return scrollableParent;
  };

  /**
   * Finds the target timeline item based on time
   * Optimized with single-pass algorithm and cached DOM queries
   * 
   * Strategy:
   * 1. First, try to find items within 1 second of target time (active item)
   * 2. If none found, find the last item before the target time (most recent past item)
   * 3. If no items before target, return null (nothing to highlight)
   * 
   * @param {number} targetTime - Target time to search for
   * @param {Array} itemsArray - Array of timeline items (optional, uses items prop if not provided)
   * @returns {Object|null} - Object with { element, time, item } or null if no match
   */
  const findTargetTimelineItem = (targetTime, itemsArray = null) => {
    const sourceItems = itemsArray || items;

    // For DOM-based search (scrolling) - use cached elements
    if (!itemsArray && containerRef.current) {
      const allTimeElements = getTimeElements();
      if (!allTimeElements || allTimeElements.length === 0) return null;

      let bestCandidate = null;
      let bestBefore = null;

      // Single-pass algorithm - O(n) instead of O(n log n)
      allTimeElements.forEach(element => {
        const elementTime = parseFloat(element.getAttribute('data-time'));
        if (!isNaN(elementTime)) {
          const diff = Math.abs(elementTime - targetTime);

          // Within 1 second - active item
          if (diff <= 1.0) {
            if (!bestCandidate || elementTime >= bestCandidate.time) {
              bestCandidate = { element, time: elementTime };
            }
          }
          // Before target time - potential fallback
          else if (elementTime < targetTime) {
            if (!bestBefore || elementTime > bestBefore.time) {
              bestBefore = { element, time: elementTime };
            }
          }
        }
      });

      // Return best candidate or fallback
      if (bestCandidate) {
        return { element: bestCandidate.element, time: bestCandidate.time, item: null };
      }
      if (bestBefore) {
        return { element: bestBefore.element, time: bestBefore.time, item: null };
      }
    }

    // For data-based search (highlighting) - optimized single-pass
    else {
      let bestCandidate = null;
      let bestBefore = null;

      sourceItems.forEach((item, index) => {
        if (item.time !== undefined && !isNaN(item.time)) {
          const diff = Math.abs(item.time - targetTime);

          // Within 1 second - active item
          if (diff <= 1.0) {
            if (!bestCandidate || item.time > bestCandidate.time ||
              (item.time === bestCandidate.time && index > bestCandidate.index)) {
              bestCandidate = { item, time: item.time, index };
            }
          }
          // Before target time - potential fallback
          else if (item.time < targetTime) {
            if (!bestBefore || item.time > bestBefore.time ||
              (item.time === bestBefore.time && index > bestBefore.index)) {
              bestBefore = { item, time: item.time, index };
            }
          }
        }
      });

      // Return best candidate or fallback
      if (bestCandidate) {
        return { element: null, time: bestCandidate.time, item: bestCandidate.item };
      }
      if (bestBefore) {
        return { element: null, time: bestBefore.time, item: bestBefore.item };
      }
    }

    // No items found - nothing to highlight
    return null;
  };

  // Sync external autoScroll prop with internal state
  useEffect(() => {
    setInternalAutoScrollEnabled(autoScroll);
  }, [autoScroll]);

  /**
   * Scrolls to a specific time element in the timeline
   * @param {number} time - Exact time to scroll to
   * @param {Object} options - Scroll options
   */
  const scrollToTime = (time, options = {}) => {
    const { behavior = 'smooth', block = 'start' } = options;
    if (!containerRef.current) return false;

    const timeElement = containerRef.current.querySelector(`[data-time="${time}"]`);
    if (timeElement) {
      timeElement.scrollIntoView({ behavior, block, inline: 'nearest' });
    }
    return false;
  };

  /**
   * Scrolls to the timeline item within 1 second of the target time
   * If multiple items have the same time, scrolls to the last one in the list
   * @param {number} targetTime - Target time to find match for
   * @param {Object} options - Scroll options
   */
  const scrollToClosestTime = (targetTime, options = {}) => {
    const { behavior = 'smooth', block = 'start', topOffset = 0 } = options;
    if (!containerRef.current) return false;

    // Use the shared function to find the target item
    const targetResult = findTargetTimelineItem(targetTime);

    // If no target found, clear the highlight
    if (!targetResult) {
      if (highlightScrollTarget) {
        setHighlightedTime(null);
      }
      return false;
    }

    const { element: targetElement, time: targetElementTime } = targetResult;

    if (targetElement) {
      // Set highlighted time if highlighting is enabled
      if (highlightScrollTarget && targetElementTime !== null) {
        setHighlightedTime(targetElementTime);
      }

      if (topOffset && topOffset > 0) {
        // Custom scroll with offset - use cached scrollable parent
        const scrollableParent = getScrollParent();

        if (scrollableParent) {
          const elementRect = targetElement.getBoundingClientRect();
          const containerRect = scrollableParent.getBoundingClientRect();
          const currentScrollTop = scrollableParent.scrollTop || window.pageYOffset;
          const targetScrollTop = currentScrollTop + elementRect.top - containerRect.top - topOffset;

          if (scrollableParent === document.documentElement || scrollableParent === document.body) {
            window.scrollTo({
              top: targetScrollTop,
              behavior
            });
          } else {
            scrollableParent.scrollTo({
              top: targetScrollTop,
              behavior
            });
          }
        }
      } else {
        // Standard scroll without offset
        targetElement.scrollIntoView({ behavior, block, inline: 'nearest' });
      }
      return true;
    }
    return false;
  };

  /**
   * Scrolls to element by ID (fallback method)
   * @param {string} id - Element ID to scroll to
   * @param {Object} options - Scroll options
   */
  const scrollToId = (id, options = {}) => {
    const { behavior = 'smooth', block = 'start' } = options;
    if (!containerRef.current) return false;

    const element = containerRef.current.querySelector(`#${id}`);
    if (element) {
      element.scrollIntoView({ behavior, block, inline: 'nearest' });
      return true;
    }
    return false;
  };

  // Set up timelineRef for auto-scroll hook
  useEffect(() => {
    timelineRef.current = {
      scrollToTime,
      scrollToClosestTime,
      scrollToId,
      getContainer: () => containerRef.current
    };
  });

  // Use the auto-scroll hook if currentTime is provided
  const autoScrollOptionsWithOffset = { ...autoScrollOptions, topOffset };
  useTimelineAutoScroll(timelineRef, currentTime, internalAutoScrollEnabled, autoScrollOptionsWithOffset);

  // Separate effect for highlighting that works independently of auto-scroll
  useEffect(() => {
    if (highlightScrollTarget && currentTime !== undefined && items.length > 0) {
      // Use the shared function to find the target item for highlighting
      const targetResult = findTargetTimelineItem(currentTime, items);

      if (targetResult && targetResult.time !== null) {
        // Update highlight with the found item (either active or last before)
        setHighlightedTime(targetResult.time);
      } else {
        // No items found (e.g., before all timeline items) - clear highlight
        setHighlightedTime(null);
      }
    } else if (!highlightScrollTarget) {
      // Clear highlight when highlighting is disabled
      setHighlightedTime(null);
    }
  }, [currentTime, highlightScrollTarget, items]);

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    scrollToTime,
    scrollToClosestTime,
    scrollToId,
    getContainer: () => containerRef.current,
    enableAutoScroll: () => setInternalAutoScrollEnabled(true),
    disableAutoScroll: () => setInternalAutoScrollEnabled(false),
    isAutoScrollEnabled: () => internalAutoScrollEnabled
  }), [internalAutoScrollEnabled]);

  return (
    <View
      as="div"
      position="relative"

      elementRef={(el) => { containerRef.current = el; }}
      // Reserve space for the icon and line on the left
      style={{
        paddingLeft: '50px',
        minHeight: '100%',
        overflow: 'visible',
        width: '100%'
      }}
    >
      {/* Auto-scroll toggle button - Fixed to Timeline container top right */}
      {showAutoScrollToggle && (
        <View
          as="div"
          className="auto-scroll-toggle"
          position="sticky"
          style={{
            top: '8px',
            right: '8px',
            zIndex: 99999,
            pointerEvents: 'auto',
            display: 'block',
            visibility: 'visible',
            marginLeft: 'auto'
          }}
        >
          <IconButton
            size="small"
            screenReaderLabel={internalAutoScrollEnabled ? "Disable auto-scroll" : "Enable auto-scroll"}
            onClick={() => setInternalAutoScrollEnabled(!internalAutoScrollEnabled)}
            color={internalAutoScrollEnabled ? "primary" : "secondary"}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          >
            <IconArrowOpenEndLine />
          </IconButton>
        </View>
      )}
      {items.map((item, index) => {
        const isHighlighted = highlightScrollTarget &&
          highlightedTime !== null &&
          item.time !== undefined &&
          item.time === highlightedTime;

        return (
          <TimelineItem
            key={item.id || `${baseId}-item-${index}`}
            item={item}
            index={index}
            totalItems={items.length}
            isHighlighted={isHighlighted}
            baseId={baseId}
          />
        );
      })}
    </View>
  );
}));

Timeline.displayName = 'Timeline';

/**
 * Custom hook for Timeline auto-scrolling with flexible data sources
 * 
 * This hook provides a clean interface for auto-scrolling that works with any data source
 * and directly manipulates the DOM without causing component re-renders.
 * 
 * @param {React.RefObject} timelineRef - Ref to Timeline component
 * @param {*} currentTime - Current time value from any source (state, props, store, etc.)
 * @param {boolean} enabled - Whether auto-scrolling is enabled
 * @param {Object} options - Auto-scroll configuration options
 * 
 * @returns {Object} Auto-scroll controls (no state that causes re-renders)
 * 
 * @example
 * // Usage with React state - no component re-renders
 * const timelineRef = useRef();
 * const [currentTime, setCurrentTime] = useState(0);
 * 
 * useTimelineAutoScroll(timelineRef, currentTime, autoScrollEnabled);
 * 
 * return <Timeline ref={timelineRef} items={timelineData} />;
 * 
 * @example
 * // Usage with Zustand store
 * const timelineRef = useRef();
 * const currentTime = usePlayerStore(state => state.currentTime);
 * 
 * useTimelineAutoScroll(timelineRef, currentTime, true);
 * 
 * @example
 * // Usage with props or context
 * const timelineRef = useRef();
 * const { currentTime } = useContext(VideoContext);
 * 
 * useTimelineAutoScroll(timelineRef, currentTime, enabled);
 */
export const useTimelineAutoScroll = (timelineRef, currentTime, enabled = false, options = {}) => {
  const {
    behavior = 'smooth',
    debounceMs = 100,
    block = 'start',
    topOffset = 0
  } = options;

  const enabledRef = useRef(enabled);
  const debounceTimerRef = useRef(null);
  const lastScrollTimeRef = useRef(0);
  const previousTimeRef = useRef(currentTime);

  // Update enabled ref when prop changes
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Auto-scroll effect triggered by currentTime changes - works with any data source
  useEffect(() => {
    // Only proceed if enabled, time changed, and timeline exists
    if (!enabledRef.current ||
      currentTime === previousTimeRef.current ||
      currentTime === undefined ||
      currentTime === null ||
      !timelineRef?.current) {
      return;
    }

    previousTimeRef.current = currentTime;

    // Debounce rapid time updates
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Direct DOM manipulation - no state updates!
      if (timelineRef.current && enabledRef.current) {
        const scrolled = timelineRef.current.scrollToClosestTime(currentTime, { behavior, block, topOffset });
        if (scrolled) {
          lastScrollTimeRef.current = currentTime;
        }
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentTime, behavior, block, debounceMs, topOffset]);

  // Return stable functions that don't cause re-renders
  return {
    enableAutoScroll: () => { enabledRef.current = true; },
    disableAutoScroll: () => { enabledRef.current = false; },
    scrollToTime: (time) => timelineRef.current?.scrollToTime(time),
    scrollToClosestTime: (time) => timelineRef.current?.scrollToClosestTime(time),
    getLastScrollTime: () => lastScrollTimeRef.current,
    isEnabled: () => enabledRef.current
  };
};

export default Timeline;
